import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { usePolicyDocument, useUpdatePolicyDocument } from '../../../../api/hooks/settings.hook';
import { JsonEditor } from '../../../../components/common/JsonEditor';

export const PolicyConfiguration = () => {
  const policy = usePolicyDocument();
  const updatePolicy = useUpdatePolicyDocument();
  const [policyText, setPolicyText] = useState('{}');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (policy.data?.policy_text) {
      setPolicyText(policy.data.policy_text);
    }
  }, [policy.data?.policy_text]);

  const handleSave = async () => {
    try {
      JSON.parse(policyText);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON';
      setValidationError(message);
      toast.error('Policy JSON must be valid before saving.');
      return;
    }
    setValidationError(null);
    try {
      await updatePolicy.mutateAsync(policyText);
      toast.success('Policy saved successfully.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update policy.';
      toast.error(message);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">
            Policy configuration
          </h1>
          <p className="text-sm text-neutral-600">
            Edit the JSON-logic policy document that governs automated actions.
          </p>
        </div>
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-4">
          {policy.isLoading ? (
            <div className="text-neutral-500 text-sm">Loading policy document…</div>
          ) : (
            <div className="space-y-3">
              <JsonEditor value={policyText} onChange={setPolicyText} height={360} />
              <div className="flex items-center justify-between">
                <div className="text-xs text-neutral-500">
                  Source:{' '}
                  <span className="font-medium text-neutral-700">{policy.data?.source}</span>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500"
                  onClick={handleSave}
                  disabled={updatePolicy.isLoading}
                >
                  {updatePolicy.isLoading ? 'Saving…' : 'Save policy'}
                </button>
              </div>
              {validationError ? <p className="text-xs text-red-600">{validationError}</p> : null}
              <p className="text-xs text-neutral-500">
                Changes are stored in{' '}
                <code className="font-mono">{policy.data?.workspace_path}</code>.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
